import { XCircleIcon } from '@heroicons/react/outline';
import {
  isNotionClientError,
  APIErrorCode,
  ClientErrorCode,
} from '@notionhq/client';
import { QueryDatabaseResponse } from '@notionhq/client/build/src/api-endpoints';
import {
  Link,
  useNavigate,
  MetaFunction,
  LinksFunction,
  LoaderFunction,
} from 'remix';
import { json, useLoaderData } from 'remix';
import invariant from 'tiny-invariant';

import { Stacked, Card, Table } from '~/components';
import { Tag } from '~/components/common/Tag';
import tableStyles from '~/styles/components/table.css';
import styles from '~/styles/routes/blog/index.css';
import {
  notion,
  colorsQuery,
  tagsQuery,
  publishedArticlesQuery,
} from '~/utils/notion.server';

type LoaderData = {
  articles: QueryDatabaseResponse;
  recentArticles: QueryDatabaseResponse;
  originalTags: QueryDatabaseResponse;
  originalColors: QueryDatabaseResponse;
  tags: QueryDatabaseResponse['results'][number]['properties'] &
    {
      id: string;
      color: QueryDatabaseResponse['results'][number]['properties'];
      isActive: boolean;
    }[];
};

// page setup
export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: styles },
    { rel: 'stylesheet', href: tableStyles },
  ];
};

export const meta: MetaFunction = () => {
  return {
    title: '@GhosteTheDev | Blog articles',
    description: 'All things created by Ghoste and where to find him!',
  };
};

const converTags = (
  tags: QueryDatabaseResponse,
  colors: QueryDatabaseResponse,
  activeTags?: string[],
) =>
  tags.results?.map(({ id: tagId, properties }) => ({
    ...properties,
    id: tagId,
    color: colors.results.find(
      ({ id }) =>
        properties?.colorRelation.type === 'relation' &&
        id === properties?.colorRelation?.relation[0]?.id,
    )?.properties,
    isActive: activeTags?.indexOf(tagId) !== -1,
  }));

const getData = async (params: URLSearchParams) => {
  let tags;
  let colors;
  let articles;

  try {
    [tags, colors, articles] = await Promise.all([
      notion.databases.query(tagsQuery),
      notion.databases.query(colorsQuery),
      notion.databases.query(publishedArticlesQuery(params.getAll('tags'))),
    ]);
  } catch (error: unknown) {
    if (isNotionClientError(error)) {
      switch (error.code) {
        case ClientErrorCode.RequestTimeout:
          throw new Error('Request timed out');
        case APIErrorCode.ObjectNotFound:
          throw new Error('Not found');
        case APIErrorCode.Unauthorized:
          throw new Error('Unauthorized');
        default:
          console.error(error.code);
      }
    } else {
      console.error(error);
    }
  }

  invariant(tags?.results && colors?.results, 'no tags or colors');

  return {
    articles,
    recentArticles: { ...articles, results: articles?.results.slice(0, 3) },
    originalTags: tags,
    originalColors: colors,
    tags: converTags(tags, colors, params.getAll('tags')),
  };
};

// SSR loader
export const loader: LoaderFunction = async ({ request }) =>
  json(await getData(new URL(request.url).searchParams));

export default function Blog() {
  const navigate = useNavigate();
  const data: LoaderData = useLoaderData();

  const activeTags = data?.tags?.filter((tag) => tag.isActive);

  return (
    <Stacked>
      <div>
        <h3> Recent articles </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
          {data?.recentArticles?.results?.map((recentArticle) => (
            <Card {...recentArticle} />
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-end space-x-2 h-8 py-1">
          {activeTags?.map((activeTag) => (
            <Tag {...activeTag} />
          ))}
          {Boolean(activeTags?.length) && (
            <Link
              className="hover:text-gray-400"
              to={{
                search: '',
              }}
            >
              <XCircleIcon className="h-6 w-6" />
            </Link>
          )}
        </div>
        <div className="overflow-hidden shadow-xl rounded-lg border border-solid border-primary">
          <Table
            data={data?.articles?.results}
            handleClick={(row) => {
              invariant(row.properties?.slug?.type === 'rich_text');
              navigate(row.properties?.slug?.rich_text[0]?.plain_text);
            }}
            renderRow={(row) => {
              invariant(row.properties?.title?.type === 'title');
              invariant(row.properties?.slug?.type === 'rich_text');
              invariant(row.properties?.tagRelation?.type === 'relation');

              const rowTags = row.properties?.tagRelation?.relation.map(
                ({ id: relationId }) =>
                  data?.tags?.find(({ id }) => id === relationId),
              );

              return (
                <>
                  <Table.Cell className="truncate">
                    {row.properties?.title?.title[0]?.plain_text}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-1">
                      {rowTags.slice(0, 3).map((tag) => (
                        <Table.Tag {...tag} />
                      ))}
                    </div>
                  </Table.Cell>
                  <Table.Actions
                    url={row.properties?.slug?.rich_text[0]?.plain_text}
                  />
                </>
              );
            }}
          />
        </div>
      </div>
    </Stacked>
  );
}
